import { ChangeDetectionStrategy, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { interval, Subject, timer } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { DarkThemeSwitch } from '../../dark-theme-switch';
import { NavbarDataService } from '../../main/navbar-data.service';
import { User } from '../../models/User';
import { ScrollPositionService } from '../../scroll-position.service';
import { CalendarService } from '../../services/calendar.service';
import { ClassesService } from '../../services/classes.service';
import { DataService } from '../../services/data-service';
import { EmergencyService } from '../../services/emergency.service';
import { HallPassesService } from '../../services/hall-passes.service';
import { HomepageService } from '../../services/homepage.service';
import { ScheduleService } from '../../services/schedule.service';
import { ScreenService } from '../../services/screen.service';
import { UserService } from '../../services/user.service';
import { PassesAnimations } from '../passes.animations';
import { FeatureFlagService, FLAGS } from 'app/services/feature-flag.service';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'app-passes-student',
	templateUrl: './passes-student.component.html',
	styleUrls: ['./passes-student.component.scss'],
	animations: [PassesAnimations.HeaderSlideInOut, PassesAnimations.HeaderSlideTopBottom, PassesAnimations.PreventInitialChildAnimation],
})
export class PassesStudentComponent implements OnInit, OnDestroy {
	@Input() effectiveUser: User;

	private scrollableAreaName = 'Passes';
	private scrollableArea: HTMLElement;

	isWeekend = false;

	@ViewChild('animatedHeader') animatedHeader: ElementRef<HTMLElement>;
	@ViewChild('passesWrapper') passesWrapper: ElementRef<HTMLElement>;

	@ViewChild('scrollableArea') set scrollable(scrollable: ElementRef) {
		if (scrollable) {
			this.scrollableArea = scrollable.nativeElement;

			const updatePosition = function () {
				const scrollObserver = new Subject();
				const initialHeight = this.scrollableArea.scrollHeight;
				const scrollOffset = this.scrollPosition.getComponentScroll(this.scrollableAreaName);

				/**
				 * If the scrollable area has static height, call `scrollTo` immediately,
				 * otherwise additional subscription will perform once if the height changes
				 */

				if (scrollOffset) {
					this.scrollableArea.scrollTo({ top: scrollOffset });
				}

				interval(50)
					.pipe(
						filter(() => {
							return initialHeight < (scrollable.nativeElement as HTMLElement).scrollHeight && scrollOffset;
						}),
						takeUntil(scrollObserver)
					)
					.subscribe((v) => {
						if (v) {
							this.scrollableArea.scrollTo({ top: scrollOffset });
							scrollObserver.next(undefined);
							scrollObserver.complete();
							updatePosition();
						}
					});
			}.bind(this);
			updatePosition();
		}
	}

	private destroy$: Subject<any> = new Subject();

	currentScrollPosition: number;

	activityStudentHomeFF = this.featureFlagService.isFeatureEnabledV2(FLAGS.ActivityStudentHome);

	@HostListener('window:scroll', ['$event'])
	scroll(event) {
		this.currentScrollPosition = event.currentTarget.scrollTop;
	}

	constructor(
		public dataService: DataService,
		public dialog: MatDialog,
		private navbarService: NavbarDataService,
		public screenService: ScreenService,
		public darkTheme: DarkThemeSwitch,
		private scrollPosition: ScrollPositionService,
		public emergencyService: EmergencyService,
		public calendarService: CalendarService,
		public homePageService: HomepageService,
		public passesService: HallPassesService,
		private homepageService: HomepageService, // this is needed to listen for encounter prevention deleting WIL passes
		public classesService: ClassesService,
		public scheduleService: ScheduleService,
		public userService: UserService,
		private featureFlagService: FeatureFlagService
	) {}

	ngOnInit(): void {
		if (moment().isoWeekday() > 5) {
			this.isWeekend = true;
		}
		this.scheduleService.mySchedulesAgendaForToday$.pipe(takeUntil(this.destroy$)).subscribe();
		timer(0, 1000)
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.homePageService.heartBeat.next(0);
			});
	}

	ngOnDestroy(): void {
		if (this.scrollableArea && this.scrollableAreaName) {
			this.scrollPosition.saveComponentScroll(this.scrollableAreaName, this.scrollableArea.scrollTop);
		}
		this.homePageService.onDestroy();
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}
}
